import React, {
  useRef,
  useEffect,
  useCallback,
  PropsWithChildren,
} from 'react';

import ReactDOM from 'react-dom';
import clsx from 'clsx';

import { Icon, IconType } from '@atoms';

import classes from './modal.module.scss';

type Props = PropsWithChildren<{
  iconType?: IconType;
  isOpen?: boolean;
  onClose?: () => void;
  disableOverscroll?: boolean;
  useFlex?: boolean;
  contentWrapperClass?: string;
  slideUp?: boolean;
  blockClosing?: boolean;
}>;

const ModalComponent = ({
  children,
  isOpen,
  onClose,
  disableOverscroll,
  useFlex,
  contentWrapperClass,
  slideUp,
  blockClosing,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    if (!blockClosing && onClose) onClose();
  }, [blockClosing, onClose]);

  // CLICK OUTSIDE MODAL
  const handleDocumentClick = useCallback(
    (e: MouseEvent) => {
      if (!contentRef.current || !e.target) return;

      const insideModalContent = contentRef.current.contains(
        e.target as Element,
      );
      if (!insideModalContent) handleClose();
    },
    [handleClose],
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      // CLOSE MODAL ON ESC
      if (e.keyCode === 27) handleClose();
    },
    [handleClose],
  );

  // COMPONENT LIFECYCLE
  useEffect(() => {
    const scroll = {
      documentElement: document.documentElement.scrollTop,
      body: document.body.scrollTop,
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keyup', handleKeyUp);
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100vh';
    document.body.scrollTop = scroll.documentElement;

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keyup', handleKeyUp);

      // Reset body styles
      document.body.style.overflow = '';
      document.body.style.height = '';

      // Reset html style
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';

      // Reset scroll
      document.body.scrollTop = scroll.body;
      document.documentElement.scrollTop = scroll.documentElement;
    };
  }, [handleDocumentClick, handleKeyUp]);

  return (
    <>
      {isOpen && (
        <div
          className={clsx(classes.wrapper, {
            [classes.noOverscroll]: disableOverscroll,
            [classes.flex]: useFlex,
          })}
        >
          <div
            className={clsx(
              classes.content,
              slideUp && classes.slideUp,
              contentWrapperClass,
            )}
            ref={contentRef}
          >
            {!blockClosing && (
              <Icon
                icon="close"
                className={classes.exitButton}
                onClick={handleClose}
              />
            )}
            {children}
          </div>
        </div>
      )}
    </>
  );
};

export const Modal = (props: Props) => {
  if (!props.isOpen) return null;

  if (typeof document !== 'undefined') {
    return ReactDOM.createPortal(<ModalComponent {...props} />, document.body);
  }

  return null;
};
